import type { ComposeQuotedBlock } from './types';
import { quotedHeaderToOutboundHtml } from './quotedHeaderMeta';

/** True when headerLines includes From/Sent/To/Subject-style metadata (not just the heading). */
export function hasQuotedHeaderMeta(headerLines: string[]): boolean {
  return headerLines.slice(1).some((line) => /^[A-Za-z]+:\s*.+/.test(line.trim()));
}

/** Remove outbound meta header cards and nested quoted wrappers from quoted body HTML. */
export function stripQuotedMetaCardsFromHtml(html: string): string {
  if (!html?.trim()) return html;
  if (typeof document === 'undefined') {
    return html
      .replace(/<div[^>]*\bclass="[^"]*email-quoted-meta-card[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<blockquote\b[^>]*class="[^"]*email-quoted-block[^"]*"[^>]*>([\s\S]*?)<\/blockquote>/gi, '$1')
      .trim();
  }

  const root = document.createElement('div');
  root.innerHTML = html.trim();
  root.querySelectorAll('.email-quoted-meta-card').forEach((el) => el.remove());
  root.querySelectorAll('blockquote.email-quoted-block').forEach((blockquote) => {
    const parent = blockquote.parentNode;
    if (!parent) return;
    while (blockquote.firstChild) {
      parent.insertBefore(blockquote.firstChild, blockquote);
    }
    blockquote.remove();
  });
  return root.innerHTML.trim();
}

function extractHeaderLinesFromMetaCard(card: Element): string[] {
  const heading =
    card.querySelector('[style*="text-transform: uppercase"] span')?.textContent?.trim() ??
    card.querySelector('span')?.textContent?.trim() ??
    'Forwarded message';
  const lines = [heading.replace(/\s+/g, ' ').trim()];
  card.querySelectorAll('table tr').forEach((row) => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 2) return;
    const label = (cells[0].textContent ?? '').trim();
    const value = (cells[1].textContent ?? '').trim();
    if (label) lines.push(`${label}: ${value}`);
  });
  if (!hasQuotedHeaderMeta(lines)) {
    const parsed = headerLinesFromPlainBlob(card.textContent ?? '');
    if (parsed) return parsed;
  }
  return lines;
}

function extractHeaderLinesFromLegacyElements(root: HTMLElement): string[] | null {
  const lines: string[] = [];
  const toRemove: Element[] = [];

  for (const child of Array.from(root.children)) {
    const text = (child.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (!text) continue;

    if (lines.length === 0) {
      if (/^(forwarded message|original message)$/i.test(text)) {
        lines.push(text);
        toRemove.push(child);
        continue;
      }
      const collapsed = headerLinesFromPlainBlob(text);
      if (collapsed) {
        toRemove.push(child);
        return collapsed;
      }
    }

    if (/^(From|Sent|To|Cc|Subject):/i.test(text)) {
      lines.push(text);
      toRemove.push(child);
      continue;
    }

    break;
  }

  if (lines.length > 1) {
    toRemove.forEach((el) => el.remove());
    return repairCollapsedHeaderLines(lines);
  }

  if (lines.length === 1) {
    const parsed = headerLinesFromPlainBlob(lines[0]);
    if (parsed) {
      toRemove.forEach((el) => el.remove());
      return parsed;
    }
  }

  return null;
}

/** Split a collapsed "Forwarded message From: … Sent: …" line into structured header lines. */
export function headerLinesFromPlainBlob(text: string): string[] | null {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  const forwardMatch = trimmed.match(
    /^(Forwarded message)\s+From:\s*(.+?)\s+Sent:\s*(.+?)\s+To:\s*(.+?)\s+Subject:\s*(.+)$/i,
  );
  if (forwardMatch) {
    return [
      forwardMatch[1],
      `From: ${forwardMatch[2].trim()}`,
      `Sent: ${forwardMatch[3].trim()}`,
      `To: ${forwardMatch[4].trim()}`,
      `Subject: ${forwardMatch[5].trim()}`,
    ];
  }
  const replyMatch = trimmed.match(
    /^(Original message)\s+From:\s*(.+?)\s+Sent:\s*(.+?)\s+To:\s*(.+?)\s+Subject:\s*(.+)$/i,
  );
  if (replyMatch) {
    return [
      replyMatch[1],
      `From: ${replyMatch[2].trim()}`,
      `Sent: ${replyMatch[3].trim()}`,
      `To: ${replyMatch[4].trim()}`,
      `Subject: ${replyMatch[5].trim()}`,
    ];
  }
  return null;
}

function repairCollapsedHeaderLines(headerLines: string[]): string[] {
  if (headerLines.length !== 1) return headerLines;
  const parsed = headerLinesFromPlainBlob(headerLines[0]);
  return parsed ?? headerLines;
}

/** Prefer the quoted block that has complete forward/reply header metadata. */
export function mergeQuotedBlocks(
  primary: ComposeQuotedBlock,
  secondary: ComposeQuotedBlock,
): ComposeQuotedBlock {
  const a = normalizeQuotedBlock(primary);
  const b = normalizeQuotedBlock(secondary);
  const aHas = hasQuotedHeaderMeta(a.headerLines);
  const bHas = hasQuotedHeaderMeta(b.headerLines);

  if (!aHas && bHas) {
    return {
      ...a,
      headerLines: b.headerLines,
      mode: b.mode ?? a.mode,
      bodyHtml: a.bodyHtml?.trim() ? a.bodyHtml : b.bodyHtml,
      bodyKind: a.bodyKind ?? b.bodyKind,
    };
  }
  if (aHas && !bHas) return a;
  if (b.headerLines.length > a.headerLines.length) {
    return { ...a, headerLines: b.headerLines, mode: b.mode ?? a.mode };
  }
  return a;
}

/** Recover missing From/Sent/To/Subject lines from saved outbound HTML when needed. */
export function enrichQuotedBlock(
  quoted: ComposeQuotedBlock | undefined,
  outboundHtml?: string | null,
): ComposeQuotedBlock | undefined {
  if (!quoted) return undefined;
  const normalized = normalizeQuotedBlock(quoted);
  if (hasQuotedHeaderMeta(normalized.headerLines)) return normalized;
  if (!outboundHtml?.trim() || typeof document === 'undefined') return normalized;

  const root = document.createElement('div');
  root.innerHTML = outboundHtml.trim();
  const blockquote = root.querySelector('blockquote.email-quoted-block');
  if (!blockquote) return normalized;

  const parsed = parseOutboundQuotedBlock(blockquote);
  return mergeQuotedBlocks(normalized, parsed);
}

/** Keep quoted.bodyHtml as original message only; header lives in headerLines. */
export function normalizeQuotedBlock(quoted: ComposeQuotedBlock): ComposeQuotedBlock {
  let headerLines = repairCollapsedHeaderLines(
    quoted.headerLines.map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean),
  );
  let bodyHtml = quoted.bodyHtml?.trim() ?? '';

  if (typeof document !== 'undefined' && bodyHtml.includes('email-quoted-meta-card')) {
    const root = document.createElement('div');
    root.innerHTML = bodyHtml;
    const metaCards = Array.from(root.querySelectorAll('.email-quoted-meta-card'));
    if (metaCards.length > 0) {
      const fromFirst = extractHeaderLinesFromMetaCard(metaCards[0]);
      if (hasQuotedHeaderMeta(fromFirst) || !hasQuotedHeaderMeta(headerLines)) {
        headerLines = fromFirst;
      }
      metaCards.forEach((card) => card.remove());
    }
    bodyHtml = root.innerHTML.trim();
  }

  bodyHtml = stripQuotedMetaCardsFromHtml(bodyHtml);
  if (!headerLines.length) {
    headerLines = [quoted.mode === 'reply' ? 'Original message' : 'Forwarded message'];
  }

  const mode =
    quoted.mode ??
    (/forward/i.test(headerLines[0] ?? '') ? 'forward' : 'reply');

  return {
    ...quoted,
    mode,
    headerLines,
    bodyHtml,
    bodyKind: quoted.bodyKind,
  };
}

/** Parse a saved outbound blockquote into a clean compose quoted block. */
export function parseOutboundQuotedBlock(blockquote: Element): ComposeQuotedBlock {
  const root = document.createElement('div');
  root.innerHTML = blockquote.innerHTML.trim();

  let headerLines: string[] = [];
  const metaCard = root.querySelector('.email-quoted-meta-card');
  if (metaCard) {
    headerLines = extractHeaderLinesFromMetaCard(metaCard);
    root.querySelectorAll('.email-quoted-meta-card').forEach((el) => el.remove());
  } else {
    const legacy = extractHeaderLinesFromLegacyElements(root);
    if (legacy) {
      headerLines = legacy;
    } else {
      const headingEl = root.querySelector('p, div, strong, b');
      const heading = headingEl?.textContent?.trim() || 'Forwarded message';
      headerLines = repairCollapsedHeaderLines([heading]);
      if (headingEl && headingEl.textContent?.trim() === heading && root.childNodes.length > 1) {
        headingEl.remove();
      }
    }
  }

  const mode: ComposeQuotedBlock['mode'] = /forward/i.test(headerLines[0] ?? '')
    ? 'forward'
    : 'reply';

  return normalizeQuotedBlock({
    mode,
    headerLines,
    bodyHtml: root.innerHTML.trim(),
    bodyKind: 'rich',
  });
}

/** Build outbound meta-card HTML for tests and verification. */
export function quotedMetaCardHtmlForHeaderLines(headerLines: string[]): string {
  return quotedHeaderToOutboundHtml(headerLines);
}
